import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment
} from "@mui/material";
import {
  Download as DownloadIcon,
  QrCode as QrCodeIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon,
  FormatAlignJustify
} from "@mui/icons-material";
import { QRCode } from 'react-qrcode-logo';
import html2canvas from 'html2canvas';
import { Configuration } from "../../../assets/data/config";
import { apiServices } from "../../../services/apiServices";
import qrLogo from "../../../assets/images/logos/qrlogo.png";
import EntityReferralStatistics from "../../AdminDashboard/Components/EntityReferralStatistics";

// ─── Teal palette to match the target design ───────────────────────────────
const TEAL = '#da7f2b';
const TEAL_LIGHT = '#e8f5f3';
const TEAL_DARK = '#145f55';

// ─── Reusable field display component ────────────────────────────────────────
const InfoField = ({ label, value, children }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", display: "block", mb: 0.5 }}
    >
      {label}
    </Typography>
    {children || (
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}
      >
        {value || "N/A"}
      </Typography>
    )}
  </Box>
);

// ─── Section wrapper component ────────────────────────────────────────────────
const SectionCard = ({ icon, title, color = "#214F7D", children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: "100%",
      backgroundColor: "#f8f9fa",
      border: "1px solid #edf0f2",
      borderRadius: 2,
      borderTop: `3px solid ${color}`,
    }}
  >
    <Typography
      variant="subtitle1"
      sx={{
        color,
        fontWeight: 600,
        mb: 2,
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: "0.95rem",
      }}
    >
      {icon} {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Paper>
);

const UserDetailsModal = ({ open, onClose, userId, onUserUpdated, peacekeeperID }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  // Referral statistics state
  const [referralStats, setReferralStats] = useState(null);
  const [referralStatsLoading, setReferralStatsLoading] = useState(false);
  const [referralStatsError, setReferralStatsError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredReferrals, setFilteredReferrals] = useState([]);

  // Download state
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const [downloadingVisitingCard, setDownloadingVisitingCard] = useState(false);
  const [downloadingQRCode, setDownloadingQRCode] = useState(false);

  useEffect(() => {
    if (open && userId) fetchUserDetails();
  }, [open, userId]);

  useEffect(() => {
    setPage(0);
    setStartDate('');
    setEndDate('');
  }, [activeTab]);

  useEffect(() => {
    if (!referralStats?.referrals) {
      setFilteredReferrals([]);
      return;
    }
    setFilteredReferrals(referralStats.referrals);
  }, [referralStats]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiServices.getAPI(`users/${userId}`);
      if (response.data.success) {
        const userData = response.data.data;
        setUser(userData);
        setEditData({
          username: userData.username || "",
          mobile: userData.mobile || "",
          country: userData.country || "",
          dob: userData.dob || "",
          activestatus: userData.activestatus || "Y",
        });
        if (userData.peacekeeper_id) fetchReferralTransactions(userData.peacekeeper_id);
      } else {
        setError("Failed to fetch user details");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setError("Error fetching user details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralStats = async (peacekeeperId) => {
    if (!peacekeeperId) return;
    setReferralStatsLoading(true);
    setReferralStatsError("");
    try {
      const response = await apiServices.getAPI(`users/referral-stats/${peacekeeperId}`);
      if (response.data.success) {
        setReferralStats(response.data.data);
        setFilteredReferrals(response.data.data?.referrals || []);
      } else {
        setReferralStatsError("Failed to fetch referral statistics");
      }
    } catch (err) {
      console.error("Error fetching referral stats:", err);
      setReferralStatsError("Error fetching referral statistics. Please try again.");
    } finally {
      setReferralStatsLoading(false);
    }
  };

  const fetchReferralTransactions = async (peacekeeperId) => {
    if (!peacekeeperId) return;
    setReferralStatsLoading(true);
    setReferralStatsError("");
    try {
      const payload = { parent_peacekeeper_id: peacekeeperId };
      const response = await apiServices.postAPI('admin/get-referral-transactions', payload);
      if (response.data.success) {
        const transactions = response.data.data || [];
        const transformedData = {
          summary: { totalReferrals: response.data.total_records || transactions.length || 0 },
          referrals: transactions,
          lastUpdated: new Date().toISOString()
        };
        setReferralStats(transformedData);
        setFilteredReferrals(transactions);
      } else {
        setReferralStatsError("Failed to fetch referral transactions");
      }
    } catch (err) {
      console.error("Error fetching referral transactions:", err);
      setReferralStatsError("Error fetching referral transactions. Please try again.");
    } finally {
      setReferralStatsLoading(false);
    }
  };

  const handleApplyFilter = () => {
    if (!referralStats?.referrals) return;
    let filtered = [...referralStats.referrals];
    if (startDate) {
      filtered = filtered.filter(r => new Date(r.created_at || r.date) >= new Date(startDate));
    }
    if (endDate) {
      filtered = filtered.filter(r => new Date(r.created_at || r.date) <= new Date(endDate + 'T23:59:59'));
    }
    setFilteredReferrals(filtered);
    setPage(0);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      setEditData({
        username: user?.username || "",
        mobile: user?.mobile || "",
        country: user?.country || "",
        dob: user?.dob || "",
        activestatus: user?.activestatus || "Y",
      });
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await apiServices.putAPI("update-user-info", { userid: userId, ...editData });
      if (response.data.success) {
        setSuccess("User updated successfully!");
        setIsEditing(false);
        await fetchUserDetails();
        if (onUserUpdated) onUserUpdated();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError("Failed to update user");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Error updating user. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => { setIsEditing(false); setError(""); setSuccess(""); };
  const formatDate = (dateString) => { if (!dateString) return "N/A"; return new Date(dateString).toLocaleDateString(); };
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "Y": return "success";
      case "N": return "error";
      default: return "default";
    }
  };

  const generateReferralLink = () => {
    if (!user) return '';
    const domain = Configuration.frontendDomain;
    const peacekeeperId = user.peacekeeper_id || 'IN-0000000-PK';
    if (user.usertype === 'Entity' && user.categoryname) {
      const cleanName = (user.name || user.username || 'User').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-');
      const cleanCategory = user.categoryname.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }
    if (user.temp_peacekeeper_id) return `${domain}/register?${user.temp_peacekeeper_id}`;
    const cleanUsername = (user.username || user.name || 'User').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-');
    return `${domain}/register?${cleanUsername}-${peacekeeperId}`;
  };

  const buildFilename = (prefix = 'VC') => {
    const safeName = (user?.username || user?.name || 'User').toString().replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '');
    const location = (user?.country || 'Location').toString().replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '');
    const peacekeeperId = (user?.peacekeeper_id || user?.userid || 'ID').toString().trim();
    return `${prefix}-${safeName}-${location}-${peacekeeperId}`;
  };

  const handleDownloadCertificate = async () => {
    if (!user?.userid) return;
    setDownloadingCertificate(true);
    try {
      const response = await apiServices.getBlobAPI(`certificate/${user.userid}`);
      if (response.data) {
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${buildFilename('Certificate')}.png`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setSuccess('Certificate downloaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else { setError('Certificate not available for this user'); }
    } catch (err) { console.error('Error downloading certificate:', err); setError('Failed to download certificate. Please try again.'); }
    finally { setDownloadingCertificate(false); }
  };

  const handleDownloadVisitingCard = async () => {
    if (!user?.userid) return;
    setDownloadingVisitingCard(true); setError(''); setSuccess('');
    try {
      let response;
      try {
        response = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
        if (!response.data || response.data.size === 0) throw new Error('Visiting card is empty');
      } catch (fetchError) {
        console.log('Visiting card not found, regenerating...'); setSuccess('Visiting card not found. Regenerating...');
        const idForRegeneration = user.peacekeeper_id || user.idNumber;
        if (!idForRegeneration) throw new Error('Unable to regenerate visiting card: No peacekeeper ID found');
        await apiServices.putAPI(`update-visiting-card/${idForRegeneration}`, { forceRegenerate: true });
        setSuccess('Visiting card regenerated. Fetching...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        response = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
        if (!response.data || response.data.size === 0) throw new Error('Visiting card was regenerated but is still empty');
      }
      if (response.data) {
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = `${buildFilename('VC')}.png`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setSuccess('Visiting card downloaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else { setError('Visiting card not available for this user'); setTimeout(() => setError(''), 3000); }
    } catch (err) {
      console.error('Error downloading visiting card:', err);
      setError('Failed to download visiting card. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally { setDownloadingVisitingCard(false); }
  };

  const handleDownloadQRCode = async () => {
    if (!user) return;
    setDownloadingQRCode(true);
    try {
      const referralLink = generateReferralLink();
      if (!referralLink) { setError('Unable to generate referral link for this user'); setDownloadingQRCode(false); return; }
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = 'position:absolute;left:-9999px;width:512px;height:512px;display:flex;justify-content:center;align-items:center;background:#fff;padding:20px;';
      document.body.appendChild(tempDiv);
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempDiv);
      root.render(React.createElement(QRCode, { value: referralLink, size: 472, logoImage: qrLogo, logoWidth: 70, logoHeight: 70, logoOpacity: 1, logoPadding: 3, logoPaddingStyle: "circle", removeQrCodeTail: true, qrStyle: 'squares', eyeRadius: 10, fgColor: "#214F7D", eyeColor: ["#214F7D", "#214F7D", "#214F7D"], backgroundOptions: { color: "#ffffff" }, ecLevel: 'H' }));
      await new Promise(resolve => setTimeout(resolve, 800));
      const canvas = await html2canvas(tempDiv, { width: 512, height: 512, backgroundColor: '#ffffff', scale: 2, useCORS: true });
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url; link.download = `QRCode-${buildFilename('Referral')}.png`;
          document.body.appendChild(link); link.click(); document.body.removeChild(link);
          URL.revokeObjectURL(url);
          setSuccess('QR code downloaded successfully!');
          setTimeout(() => setSuccess(''), 3000);
        } else { setError('Failed to generate QR code image'); }
        root.unmount(); document.body.removeChild(tempDiv); setDownloadingQRCode(false);
      }, 'image/png', 1.0);
    } catch (err) { console.error('Error generating QR code:', err); setError('Failed to generate QR code. Please try again.'); setDownloadingQRCode(false); }
  };

  const buildReferralRows = (referrals = []) =>
    referrals.map((r, i) => ({
      id: r.reference_by,
      name: r.username,
      email: r.emailid || 'N/A',
      date: r.created_at
        ? new Date(r.created_at || r.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        : 'N/A',
    }));

  if (!open) return null;

  const paginatedReferrals = buildReferralRows(filteredReferrals).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh", minWidth: "900px" } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", display: "flex", justifyContent: "space-between", alignItems: "center", py: 2.5 }}>
        <Typography variant="h5" fontWeight="300" color="text.primary">
          {isEditing ? "Edit User Profile" : "User Details"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {user && !isEditing && (
            <>
              <Button variant="outlined" onClick={handleDownloadCertificate} disabled={downloadingCertificate}
                startIcon={downloadingCertificate ? <CircularProgress size={16} /> : <DownloadIcon />} size="medium"
                sx={{ borderColor: "#3A8139", color: "#3A8139", borderRadius: "50px", px: 3, py: 1, textTransform: "none", fontWeight: 500, "&:hover": { borderColor: "#2d652c", backgroundColor: "rgba(58,129,57,0.04)" }, "&:disabled": { borderColor: "#ccc", color: "#999" } }}>
                {downloadingCertificate ? "Downloading..." : "Download Certificate"}
              </Button>
              <Button variant="outlined" onClick={handleDownloadVisitingCard} disabled={downloadingVisitingCard}
                startIcon={downloadingVisitingCard ? <CircularProgress size={16} /> : <DownloadIcon />} size="medium"
                sx={{ borderColor: "#214F7D", color: "#214F7D", borderRadius: "50px", px: 3, py: 1, textTransform: "none", fontWeight: 500, "&:hover": { borderColor: "#1a3f63", backgroundColor: "rgba(33,79,125,0.04)" }, "&:disabled": { borderColor: "#ccc", color: "#999" } }}>
                {downloadingVisitingCard ? "Downloading..." : "Download Visiting Card"}
              </Button>
              <Button variant="outlined" onClick={handleDownloadQRCode} disabled={downloadingQRCode}
                startIcon={downloadingQRCode ? <CircularProgress size={16} /> : <QrCodeIcon />} size="medium"
                sx={{ borderColor: "#FF6B35", color: "#FF6B35", borderRadius: "50px", px: 3, py: 1, textTransform: "none", fontWeight: 500, "&:hover": { borderColor: "#E55A2B", backgroundColor: "rgba(255,107,53,0.04)" }, "&:disabled": { borderColor: "#ccc", color: "#999" } }}>
                {downloadingQRCode ? "Generating..." : "Download QR Code"}
              </Button>
            </>
          )}
          <Button variant="outlined" onClick={onClose} size="medium"
            sx={{ borderColor: "#6c757d", color: "#6c757d", borderRadius: "50px", px: 3, py: 1, textTransform: "none", fontWeight: 500, "&:hover": { borderColor: "#495057", backgroundColor: "rgba(108,117,125,0.04)" } }}>
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">Loading user details...</Typography>
            </Box>
          </Box>
        ) : user ? (
          <>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} aria-label="user details tabs"
                sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "0.95rem", minHeight: 48 } }}>
                <Tab label="User Details" />
                <Tab label="Referral Statistics" />
                <Tab label="Entity Referral Statistics" />
              </Tabs>
            </Box>

            {/* ══════════════════ TAB 0: USER DETAILS — 4 SECTIONS ══════════════════ */}
            {activeTab === 0 && (
              <Box sx={{ pb: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {/* 2×2 CSS grid — always 2 columns regardless of breakpoints */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>

                  {/* ── ROW 1, COL 1: Identity & Account ── */}
                  <Box>
                    <SectionCard icon="🪪" title="Account Information" color="#214F7D">
                      <Grid item xs={6}>
                        <InfoField label="IAPM ID">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#214F7D", fontSize: "0.9rem", p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}>
                            {user.peacekeeper_id || "N/A"}
                          </Typography>
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Name">
                          {isEditing ? (
                            <TextField fullWidth value={editData.username} onChange={(e) => handleInputChange("username", e.target.value)} size="small" sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }} />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}>{user.username || "N/A"}</Typography>
                          )}
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Email">
                          <Typography variant="body2" sx={{ color: "#6c757d", p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center", wordBreak: "break-all" }}>{user.email || "N/A"}</Typography>
                        </InfoField>
                      </Grid>
                      <InfoField label="Payment Status">
                        <FormControl fullWidth size="small">
                          <Select
                            value={user.payment_status || 'PENDING'}
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              setSaving(true); setError(''); setSuccess('');
                              try {
                                const response = await apiServices.putAPI('users/enhanced', { peacekeeper_id: user.peacekeeper_id, payment_status: newStatus });
                                if (response.data.message && response.data.message.includes('successfully')) {
                                  setUser(prev => ({ ...prev, payment_status: newStatus }));
                                  setSuccess('Payment status updated successfully!');
                                  if (onUserUpdated) onUserUpdated();
                                  setTimeout(() => setSuccess(''), 3000);
                                } else { throw new Error('Failed to update payment status'); }
                              } catch (error) { console.error('Error updating payment status:', error); setError('Failed to update payment status. Please try again.'); }
                              finally { setSaving(false); }
                            }}
                            disabled={saving}
                            sx={{ backgroundColor: "#fff", minHeight: "36px", fontSize: "0.8rem", width:"12rem" }}
                          >
                            <MenuItem value="PENDING">PENDING</MenuItem>
                            <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                            <MenuItem value="COMPLIMENTARY">COMPLIMENTARY</MenuItem>
                            <MenuItem value="FAILED">FAILED</MenuItem>
                          </Select>
                        </FormControl>
                      </InfoField>
                      <Grid item xs={6}>
                        <InfoField label="Account Status">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select value={editData.activestatus} onChange={(e) => handleInputChange("activestatus", e.target.value)} sx={{ backgroundColor: "#fff", fontSize: "0.8rem" }}>
                                <MenuItem value="Y" sx={{ fontSize: "0.8rem" }}>Active</MenuItem>
                                <MenuItem value="N" sx={{ fontSize: "0.8rem" }}>Inactive</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Box sx={{ minHeight: "36px", display: "flex", alignItems: "center" }}>
                              <Chip label={user.activestatus === "Y" ? "Active" : "Inactive"} color={getStatusColor(user.activestatus)} size="small" sx={{ fontWeight: 600, fontSize: "0.75rem", px: 1 }} />
                            </Box>
                          )}
                        </InfoField>
                      </Grid>
                    </SectionCard>
                  </Box>

                  {/* ── ROW 1, COL 2: Personal Information ── */}
                  <Box >
                    <SectionCard icon="👤" title="Personal Information" color="#3A8139" >
                      <Grid item xs={6}>
                        <InfoField label="Mobile Number">
                          {isEditing ? (
                            <TextField fullWidth value={editData.mobile} onChange={(e) => handleInputChange("mobile", e.target.value)} size="small" sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }} />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}>{user.mobile || "N/A"}</Typography>
                          )}
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Date of Birth">
                          {isEditing ? (
                            <TextField fullWidth type="date" value={editData.dob} onChange={(e) => handleInputChange("dob", e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }} />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}>{formatDate(user.dob)}</Typography>
                          )}
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Country">
                          {isEditing ? (
                            <TextField fullWidth value={editData.country} onChange={(e) => handleInputChange("country", e.target.value)} size="small" sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }} />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}>{user.country || "N/A"}</Typography>
                          )}
                        </InfoField>
                      </Grid>                     
                      <Grid item xs={6}>
                        <InfoField label="Gender">
                          <Typography variant="body2" sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}>{user.gender || "N/A"}</Typography>
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Referred By">
                          <Typography variant="body2" sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center", color: user.reference_by ? "#214F7D" : "#6c757d" }}>{user.reference_by || "N/A"}</Typography>
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Created Date">
                          <Typography variant="body2" sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}>{formatDate(user.created_at)}</Typography>
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Last Updated">
                          <Typography variant="body2" sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center" }}>{formatDate(user.updated_at)}</Typography>
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Total Referrals">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#9C27B0", p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center", fontSize: "1rem" }}>
                            {referralStats?.summary?.totalReferrals ?? user.total_referrals ?? "N/A"}
                          </Typography>
                        </InfoField>
                      </Grid>
                      <Grid item xs={6}>
                        <InfoField label="Referral Link">
                          <Typography
                            variant="body2"
                            title={generateReferralLink()}
                            sx={{ fontWeight: 500, p: 1, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e9ecef", minHeight: "36px", display: "flex", alignItems: "center", fontSize: "0.72rem", color: "#214F7D", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
                            onClick={() => { const link = generateReferralLink(); if (link) navigator.clipboard?.writeText(link); }}
                          >
                            {generateReferralLink() || "N/A"}
                          </Typography>
                        </InfoField>
                      </Grid>
                    </SectionCard>
                  </Box>
                </Box>

                {/* Edit / Save / Cancel Actions (shown only in view mode — edit buttons in footer) */}
                {!isEditing && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleEditToggle}
                      sx={{
                        background: "linear-gradient(to right, #214F7D, #3A8139)",
                        color: "white",
                        borderRadius: "50px",
                        px: 4,
                        py: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": { background: "linear-gradient(to right, #1a3f63, #2d6a2e)" }
                      }}
                    >
                      ✏️ Edit User
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* ══════════════════ TAB 1: REFERRAL STATISTICS ══════════════════ */}
            {activeTab === 1 && (
              <>
                {referralStatsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <CircularProgress size={40} sx={{ mb: 2, color: TEAL }} />
                      <Typography variant="body1" color="text.secondary">Loading referral statistics...</Typography>
                    </Box>
                  </Box>
                ) : referralStatsError ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography variant="h6" color="error" sx={{ mb: 2 }}>{referralStatsError}</Typography>
                    <Button variant="outlined" onClick={() => user?.peacekeeper_id && fetchReferralStats(user.peacekeeper_id)} size="medium" sx={{ mt: 2 }}>Retry</Button>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'stretch', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                      <Box sx={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <Box sx={{ background: 'white', borderRadius: 2, p: 2, minWidth: 130, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, boxShadow: `0 4px 14px rgba(26,122,110,0.35)` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PeopleIcon sx={{ color: '#000000', fontSize: 28 }} />
                            <Typography variant="h3" sx={{ color: '#000000', fontWeight: 800, lineHeight: 1, fontSize: '2.2rem' }}>
                              {referralStats?.summary?.totalReferrals || 0}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600, fontSize: '0.7rem', textAlign: 'center', letterSpacing: 0.5 }}>
                            Total Referral
                          </Typography>
                        </Box>
                        <Box sx={{ background: 'white', borderRadius: 2, p: 2, minWidth: 130, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, boxShadow: `0 4px 14px rgba(26,122,110,0.35)` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon sx={{ color: '#000000', fontSize: 24 }} />
                            <Typography variant="h6" sx={{ color: '#000000', fontWeight: 700, lineHeight: 1, fontSize: '1rem' }}>
                              {referralStats?.lastUpdated
                                ? new Date(referralStats.lastUpdated).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                                : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 600, fontSize: '0.7rem', textAlign: 'center', letterSpacing: 0.5 }}>
                            Last Updated
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, border: '1px solid #e0e0e0', borderRadius: 2, p: 2, backgroundColor: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', fontSize: '0.85rem' }}>Filter by Date Range:</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <TextField type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ fontSize: 16, color: '#888' }} /></InputAdornment> }}
                            sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { fontSize: '0.8rem', '&:hover fieldset': { borderColor: TEAL }, '&.Mui-focused fieldset': { borderColor: TEAL } } }} />
                          <TextField type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ fontSize: 16, color: '#888' }} /></InputAdornment> }}
                            sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { fontSize: '0.8rem', '&:hover fieldset': { borderColor: TEAL }, '&.Mui-focused fieldset': { borderColor: TEAL } } }} />
                          <Button variant="contained" onClick={handleApplyFilter} startIcon={<FilterIcon />}
                            sx={{ backgroundColor: '#e8531a', color: '#ffffff', fontWeight: 600, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 2.5, '&:hover': { backgroundColor: '#c94415' } }}>
                            Apply Filter
                          </Button>
                        </Box>
                      </Box>
                    </Box>

                    <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['REFERRAL ID', 'NAME', 'EMAIL ID', 'DATE'].map((col) => (
                                <TableCell key={col} sx={{ backgroundColor: TEAL, color: '#fff', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.6px', py: 1.5, px: 2, borderBottom: 'none' }}>
                                  {col}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedReferrals.length > 0 ? (
                              paginatedReferrals.map((row, index) => (
                                <TableRow key={index} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }, '&:nth-of-type(even)': { backgroundColor: '#ffffff' }, '&:hover': { backgroundColor: TEAL_LIGHT + ' !important' }, transition: 'background-color 0.15s ease' }}>
                                  <TableCell sx={{ fontWeight: 600, color: '#333', fontSize: '0.85rem', py: 1.3, px: 2, borderBottom: '1px solid #f0f0f0' }}>{row.id}</TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem', color: '#444', py: 1.3, px: 2, borderBottom: '1px solid #f0f0f0' }}>{row.name}</TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem', color: '#555', py: 1.3, px: 2, borderBottom: '1px solid #f0f0f0' }}>{row.email}</TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem', color: '#555', py: 1.3, px: 2, borderBottom: '1px solid #f0f0f0' }}>{row.date}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 5, color: '#999' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {filteredReferrals.length === 0 && (startDate || endDate) ? 'No referrals found in the selected date range.' : 'No referral data available for this user.'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2, py: 1, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          component="div"
                          count={filteredReferrals.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={(e, newPage) => setPage(newPage)}
                          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                          labelRowsPerPage="Rows per page:"
                          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.8rem', color: '#666' }, '& .MuiTablePagination-actions .MuiIconButton-root': { color: TEAL }, '& .MuiTablePagination-select': { color: TEAL, fontWeight: 600 } }}
                        />
                      </Box>
                    </Paper>
                  </Box>
                )}
              </>
            )}

            {/* ══════════════════ TAB 2: ENTITY REFERRAL STATISTICS ══════════════════ */}
            {activeTab === 2 && (
              <Box sx={{ mt: 2, mb: 4 }}>
                <EntityReferralStatistics userId={user.peacekeeper_id} />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>No user data available</Typography>
            <Typography variant="body2" color="text.secondary">Please try refreshing the page or contact support if the issue persists.</Typography>
          </Box>
        )}
      </DialogContent>

      {/* Dialog Actions (edit mode) */}
      {isEditing && (
        <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", gap: 2 }}>
          <Button onClick={handleCancelEdit} disabled={saving} variant="outlined" size="large"
            sx={{ borderColor: "#6c757d", color: "#6c757d", borderRadius: "50px", px: 4, py: 1.5, textTransform: "none", fontWeight: 500 }}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} variant="contained" size="large"
            sx={{ background: "linear-gradient(to right, #214F7D, #3A8139)", color: "white", borderRadius: "50px", px: 4, py: 1.5, textTransform: "none", fontWeight: 600, "&:hover": { background: "linear-gradient(to right, #1a3f63, #2d6a2e)" }, "&:disabled": { background: "#ccc", color: "#666" } }}>
            {saving ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Saving Changes...
              </Box>
            ) : "Save Changes"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UserDetailsModal;